import { ScheduleType } from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import useFulfillment from '@platform-ui-common/hooks/contexts/useFulfillment.js';
import useProgram from '@platform-ui-common/hooks/contexts/useProgram.js';
import useCustomPageTitle from '@platform-ui-common/hooks/useCustomPageTitle.js';
import { displayPaymentDetails } from '@platform-ui-common/utilities/payment.js';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import useCase from '@platform-ui/case/hooks/useCase.js';
import { makeRoute, useUserHomeRoute } from '@platform-ui/utilities/Routes.js';
import { Box, Flex, Heading, Text } from '@radix-ui/themes';
import { IKImage } from 'imagekitio-react';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styles from './FulfillmentDetails.module.css';
import RecurringPaymentOverview from './RecurringPaymentOverview.js';
import OutcomeMessage from './OutcomeMessage.js';
import ContactInfoMessage from './ContactInfoMessage.js';
import PaymentDetailsPanel from './PaymentDetailsPanel.js';

export default function FulfillmentDetails(): JSX.Element {
  const history = useHistory();
  const userHomeRoute = useUserHomeRoute();
  const program = useProgram();
  const { case_ } = useCase();
  const fulfillment = useFulfillment();
  const withPaymentDetails = displayPaymentDetails({ program, fulfillment, case: case_ });
  const heading = `${program?.name} Payment Details`;
  useCustomPageTitle(heading);

  useEffect(() => {
    if (!program || !case_ || !fulfillment) history.push(userHomeRoute);
  }, [case_, program, fulfillment, history, userHomeRoute]);

  if (!program || !case_ || !fulfillment) return null;

  return (
    <Flex direction="column" align="center">
      <Flex direction="column" align="center" mb="5">
        <Box maxHeight="8rem" mb="4">
          <IKImage
            src={program.heroImage}
            alt={`${program.name} logo`}
            className={`${'max-h-full h-fill-available'} ${styles.FulfillmentDetailsHeroImage}`}
            urlEndpoint="https://ik.imagekit.io/bybeam/"
          />
        </Box>
        <Heading size="6">{heading}</Heading>
      </Flex>
      <Flex maxWidth="48rem" direction="column" gap="4" align="center" width="100%">
        <Flex
          direction="column"
          gap="2"
          px="6"
          py="4"
          width="100%"
          className={styles.statusContainer}
        >
          <Flex justify="between" gap="2">
            <Heading size="5" weight="regular" align="center">
              Payment Status
            </Heading>
            <Text size="3">
              <strong>Display ID: {fulfillment.displayId}</strong>
            </Text>
          </Flex>
          <Flex direction="column" gap="2">
            <OutcomeMessage />
            <Text>
              <ContactInfoMessage />
            </Text>
          </Flex>
        </Flex>
        {withPaymentDetails && (
          <>
            <PaymentDetailsPanel />
            {fulfillment.scheduleType === ScheduleType.Recurring && <RecurringPaymentOverview />}
          </>
        )}
        <Button link={makeRoute(ApplicantRoutes.CASE_DETAILS, { caseId: case_.id })}>
          Back To Case Details
        </Button>
        <Button link={ApplicantRoutes.APPLICATION_PORTAL} variant="outline">
          Back To Application Portal
        </Button>
      </Flex>
    </Flex>
  );
}
